import { ArrowLeftIcon } from "lucide-react"
import { useSearchParams } from "react-router"
import { DocsRouterLink as RouterLink } from "~/docs-data/docs-link"

/**
 * Small back affordance for article-style docs pages. Returns to `fallback`
 * (e.g. the guides index) unless the page was reached from a special place
 * that passed a `?back=<path>` param — then it returns there instead.
 *
 * Only internal docs paths are honored (leading slash + a /docs segment), so
 * a crafted URL can't turn this into a redirect to an arbitrary site. The
 * `back` value may already carry the secret docs prefix — DocsRouterLink
 * leaves already-prefixed paths untouched, so both bare and prefixed values
 * work.
 */
export function DocsBackLink({
  fallback = "/docs/guides",
  label = "Back",
}: {
  fallback?: string
  label?: string
}) {
  const [params] = useSearchParams()
  const back = params.get("back")
  const isInternalDocsPath =
    !!back && /^\/[^/]/.test(back) && /\/docs(\/|$)/.test(back)
  const target = isInternalDocsPath ? back : fallback

  return (
    <RouterLink
      to={target}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
    >
      <ArrowLeftIcon className="w-3.5 h-3.5" />
      {label}
    </RouterLink>
  )
}
